import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lightbulb, X, RefreshCw, Sparkles } from 'lucide-react';
import { aiAPI } from '../../services/api';

interface NutritionTip {
  id: string;
  title: string;
  content: string;
  category: 'general' | 'cooking' | 'nutrition' | 'planning';
}

const SmartNutritionTips: React.FC = () => {
  const [currentTip, setCurrentTip] = useState<NutritionTip | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const staticTips: NutritionTip[] = [
    {
      id: '1',
      title: 'Hydration Timing',
      content: 'Drink water 30 minutes before meals to aid digestion and help control portion sizes. This simple habit can improve nutrient absorption.',
      category: 'nutrition'
    },
    {
      id: '2',
      title: 'Colorful Plate Rule',
      content: 'Aim for at least 3 different colors on your plate. Each color represents different phytonutrients that support various aspects of health.',
      category: 'planning'
    },
    {
      id: '3',
      title: 'Protein Distribution',
      content: 'Spread protein intake throughout the day (20-30g per meal) for optimal muscle protein synthesis and sustained energy levels.',
      category: 'nutrition'
    },
    {
      id: '4',
      title: 'Cooking Temperature',
      content: 'Steam or lightly sauté vegetables to preserve water-soluble vitamins like vitamin C and B vitamins that are lost in high-heat cooking.',
      category: 'cooking'
    }
  ];

  const generateAITip = async () => {
    setIsLoading(true);
    try {
      const response = await aiAPI.chat(
        "Generate a short, practical nutrition tip (2-3 sentences) that users can implement today. Focus on evidence-based advice for healthy eating, cooking, or meal planning.",
        []
      );
      
      const aiTip: NutritionTip = {
        id: Date.now().toString(),
        title: 'AI Nutrition Insight',
        content: response.data.response,
        category: 'general'
      };
      
      setCurrentTip(aiTip);
    } catch (error) {
      // Fallback to static tip if AI fails
      const randomTip = staticTips[Math.floor(Math.random() * staticTips.length)];
      setCurrentTip(randomTip);
    } finally {
      setIsLoading(false);
    }
  };

  const showRandomTip = () => {
    const randomTip = staticTips[Math.floor(Math.random() * staticTips.length)];
    setCurrentTip(randomTip);
    setIsVisible(true);
  };

  useEffect(() => {
    // Show first tip after 10 seconds
    const initialTimer = setTimeout(() => {
      showRandomTip();
    }, 10000);

    // Show tips every 5 minutes
    const intervalTimer = setInterval(() => {
      if (!isVisible) {
        showRandomTip();
      }
    }, 300000);

    return () => {
      clearTimeout(initialTimer);
      clearInterval(intervalTimer);
    };
  }, [isVisible]);

  const getCategoryColor = (category: string) => {
    switch (category) {
      case 'nutrition': return 'from-green-500 to-teal-500';
      case 'cooking': return 'from-orange-500 to-red-500';
      case 'planning': return 'from-blue-500 to-cyan-500';
      default: return 'from-purple-500 to-pink-500';
    }
  };

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'nutrition': return '🥗';
      case 'cooking': return '👨‍🍳';
      case 'planning': return '📅';
      default: return '💡';
    }
  };

  if (!currentTip || !isVisible) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.9 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.9 }}
        transition={{ duration: 0.5, type: "spring" }}
        className="fixed bottom-24 left-6 z-40 max-w-sm"
      >
        <div className={`bg-gradient-to-r ${getCategoryColor(currentTip.category)} p-0.5 rounded-2xl shadow-2xl`}>
          <div className="bg-white rounded-2xl p-6 relative">
            {/* Header */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center space-x-2">
                <span className="text-2xl">{getCategoryIcon(currentTip.category)}</span>
                <div className={`w-8 h-8 bg-gradient-to-r ${getCategoryColor(currentTip.category)} rounded-full flex items-center justify-center`}>
                  <Lightbulb className="w-4 h-4 text-white" />
                </div>
                <span className="font-semibold text-gray-800">Nutrition Tip</span>
              </div>
              
              <div className="flex items-center space-x-1">
                <button
                  onClick={generateAITip}
                  disabled={isLoading}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Get AI-powered tip"
                >
                  {isLoading ? (
                    <RefreshCw className="w-4 h-4 animate-spin" />
                  ) : (
                    <Sparkles className="w-4 h-4" />
                  )}
                </button>
                <button
                  onClick={() => setIsVisible(false)}
                  className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-3">
              <h3 className="font-semibold text-gray-900">{currentTip.title}</h3>
              <p className="text-gray-700 text-sm leading-relaxed">{currentTip.content}</p>
            </div>

            {/* Footer */}
            <div className="mt-4 pt-3 border-t border-gray-100">
              <div className="flex items-center justify-between">
                <span className="text-xs text-gray-500 capitalize">{currentTip.category} tip</span>
                <div className="flex items-center space-x-1">
                  <Sparkles className="w-3 h-3 text-purple-400" />
                  <span className="text-xs text-gray-500">FoodMind AI</span>
                </div>
              </div>
            </div>

            {/* Animated border */}
            <div className="absolute inset-0 rounded-2xl">
              <div className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${getCategoryColor(currentTip.category)} opacity-20 animate-pulse`} />
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default SmartNutritionTips;